import { EventEmitter } from 'events';
import { CODE_TO_NETWORK_CONDITION, NETWORK_CONDITION, NETWORK_CONDITION_CODE, NETWORK_CONDITION_REASON, NETWORK_CONDITION_SOURCE } from '../../helpers/networkConditionConstants';
import { CongestionLevels } from '../peer_connection/congestionLevel/congestionLevels';
import sessionObjects from '../../ot/session/objects';
import createLogger from '../../helpers/log';

const SENDER_SIDE_STATS_TTL = 30000; // 30 seconds

const logging = createLogger('NetworkConditionChecker');

const getNetworkConditionFromCongestionLevel = (
  congestionLevel,
  stream,
  peerEnd,
  isP2P,
  publisherNetworkCondition
) => {
  if (!congestionLevel) {
    // If there is no congestion but audio fallback is enabled, report good condition
    if ((peerEnd === 'MANTIS' && stream.subscriberAudioFallbackEnabled) || (peerEnd === 'PUBLISHER' && stream.publisherAudioFallbackEnabled)) {
      return NETWORK_CONDITION_CODE.GOOD;
    }
    return NETWORK_CONDITION_CODE.UNKNOWN;
  }
  if (congestionLevel === CongestionLevels.CRITICAL) {
    // If congestion level is critical, we can assign the condition to publisher or mantis based on video presence
    if (peerEnd === 'MANTIS') {
      if (stream.hasVideo || !stream.publisherAudioFallbackEnabled) {
        return NETWORK_CONDITION_CODE.CRITICAL;
      }
      return NETWORK_CONDITION_CODE.UNKNOWN;
    }
    // publisher
    if (!stream.hasVideo || isP2P || !stream.subscriberAudioFallbackEnabled) {
      return NETWORK_CONDITION_CODE.CRITICAL;
    }
    return NETWORK_CONDITION_CODE.UNKNOWN;
  }
  // If congestion level is warning, we don't have enough info to assign it to mantis or publisher,
  // unless...
  // - Is P2P (local and remote legs are the same)
  if (isP2P) {
    return NETWORK_CONDITION_CODE.WARNING;
  }
  // - Is local leg and, publisher network condition is known and different from WARNING or PAF is disabled
  if (
    peerEnd === 'MANTIS' &&
      (
        (
          publisherNetworkCondition !== NETWORK_CONDITION_CODE.UNKNOWN &&
          publisherNetworkCondition !== NETWORK_CONDITION_CODE.WARNING
        ) ||
        !stream.publisherAudioFallbackEnabled
      )
  ) {
    return NETWORK_CONDITION_CODE.WARNING;
  }
  // - Is remote leg and SAF is disabled (there is no network condition from Mantis yet)
  if (peerEnd === 'PUBLISHER' && !stream.subscriberAudioFallbackEnabled) {
    return NETWORK_CONDITION_CODE.WARNING;
  }
  return NETWORK_CONDITION_CODE.UNKNOWN;
};

const isDegraded = networkCondition =>
  networkCondition === NETWORK_CONDITION.CRITICAL ||
  networkCondition === NETWORK_CONDITION.WARNING ||
  networkCondition === NETWORK_CONDITION.FAIR;

const getNetworkCondition = (
  senderStats,
  congestionLevel,
  stream,
  peerEnd,
  isP2P,
  publisherNetworkCondition
) => {
  const {
    networkCondition: networkConditionCode = NETWORK_CONDITION_CODE.UNKNOWN,
    networkConditionReason,
  } = senderStats || {};
  const audioFallbackConditionCode = getNetworkConditionFromCongestionLevel(
    congestionLevel,
    stream,
    peerEnd,
    isP2P,
    publisherNetworkCondition
  );
  const degradedFromSenderStats = networkConditionCode === NETWORK_CONDITION_CODE.CRITICAL
    || networkConditionCode === NETWORK_CONDITION_CODE.WARNING;
  const degradedFromAudioFallback = audioFallbackConditionCode === NETWORK_CONDITION_CODE.CRITICAL
    || audioFallbackConditionCode === NETWORK_CONDITION_CODE.WARNING;
  // Degradation from audio fallback takes precedence over sender stats
  if (degradedFromAudioFallback) {
    return {
      networkCondition: CODE_TO_NETWORK_CONDITION[audioFallbackConditionCode],
      networkConditionReason: degradedFromSenderStats && networkConditionReason ?
        networkConditionReason : NETWORK_CONDITION_REASON.UNKNOWN,
    };
  }
  // If we have audio fallback enabled and says there is no degradation, we can not trust sender stats if they say there is degradation
  if (
    audioFallbackConditionCode === NETWORK_CONDITION_CODE.UNKNOWN ||
    (networkConditionCode !== NETWORK_CONDITION_CODE.UNKNOWN && !degradedFromSenderStats)
  ) {
    const networkCondition =
      CODE_TO_NETWORK_CONDITION[networkConditionCode] || NETWORK_CONDITION.UNKNOWN;
    return {
      networkCondition,
      networkConditionReason: networkConditionReason ||
        (!isDegraded(networkCondition) && networkCondition !== NETWORK_CONDITION.UNKNOWN ?
          NETWORK_CONDITION_REASON.NONE : NETWORK_CONDITION_REASON.UNKNOWN),
    };
  }
  return {
    networkCondition:
      CODE_TO_NETWORK_CONDITION[audioFallbackConditionCode] || NETWORK_CONDITION.UNKNOWN,
    networkConditionReason: audioFallbackConditionCode !== NETWORK_CONDITION_CODE.UNKNOWN
      ? NETWORK_CONDITION_REASON.NONE : NETWORK_CONDITION_REASON.UNKNOWN,
  };
};

const getDegradationSourceBySignalingNetworkScore = (publisherSenderStats, session) => {
  if (!publisherSenderStats?.signalingNetworkScore) {
    return NETWORK_CONDITION_SOURCE.BOTH_OR_UNCLEAR;
  }
  const subscriberSignalingNetworkScore = session._.getSocket().getSignalingNetworkQualityScore();
  if (publisherSenderStats.signalingNetworkScore === subscriberSignalingNetworkScore) {
    return NETWORK_CONDITION_SOURCE.BOTH_OR_UNCLEAR;
  }
  if (publisherSenderStats.signalingNetworkScore < subscriberSignalingNetworkScore) {
    return NETWORK_CONDITION_SOURCE.REMOTE;
  }
  return NETWORK_CONDITION_SOURCE.LOCAL;
};

const isSubscriberWithNetworkDegradation = (subscriber, subscriberId) => {
  if (subscriber.widgetId === subscriberId) {
    return false;
  }
  const { networkCondition } = subscriber._.getNetworkConditionStats().transport;
  return isDegraded(networkCondition);
};

const getP2PNetworkDegradationSource = (
  publisherNetworkCondition,
  publisherSenderStats,
  session,
  subscriberId
) => {
  if (publisherNetworkCondition === NETWORK_CONDITION.UNKNOWN) {
    // Nothing we can do, no sender side stats nor PAF
    return NETWORK_CONDITION_SOURCE.NONE;
  }
  if (!isDegraded(publisherNetworkCondition)) {
    return NETWORK_CONDITION_SOURCE.NONE;
  }
  const degradationSourceBySignaling = getDegradationSourceBySignalingNetworkScore(
    publisherSenderStats,
    session
  );
  const numOfOtherSubscribers = sessionObjects.subscribers.length() - 1; // exclude self
  if (
    degradationSourceBySignaling !== NETWORK_CONDITION_SOURCE.BOTH_OR_UNCLEAR ||
    numOfOtherSubscribers === 0
  ) {
    return degradationSourceBySignaling;
  }
  // Check if there are more subscribers with or without degradation
  const numOfSubscribersWithDegradation = sessionObjects.subscribers.where(
    subscriber => isSubscriberWithNetworkDegradation(subscriber, subscriberId)
  ).length;
  if (numOfSubscribersWithDegradation >= numOfOtherSubscribers / 2) {
    return NETWORK_CONDITION_SOURCE.LOCAL;
  }
  return NETWORK_CONDITION_SOURCE.REMOTE;
};

const getRoutedNetworkDegradationSource = (localCondition, remoteCondition, congestionLevel) => {
  const localDegraded = isDegraded(localCondition);
  const remoteDegraded = isDegraded(remoteCondition);
  if (localDegraded) {
    return remoteDegraded ?
      NETWORK_CONDITION_SOURCE.BOTH_OR_UNCLEAR : NETWORK_CONDITION_SOURCE.LOCAL;
  }
  if (remoteDegraded) {
    return NETWORK_CONDITION_SOURCE.REMOTE;
  }
  if (congestionLevel) {
    // if there is congestion but neither local nor remote are degraded, we cannot determine the source
    return NETWORK_CONDITION_SOURCE.BOTH_OR_UNCLEAR;
  }
  return NETWORK_CONDITION_SOURCE.NONE;
};

const hasNetworkConditionChanged = (currentNetworkConditionStats, newNetworkConditionStats) => {
  if (!currentNetworkConditionStats) {
    return false;
  }
  const {
    transport: currentLocal,
    remotePublisherTransport: currentRemote,
    networkDegradationSource: currentSource,
  } = currentNetworkConditionStats;
  const {
    transport: newLocal,
    remotePublisherTransport: newRemote,
    networkDegradationSource: newSource,
  } = newNetworkConditionStats;
  return (
    currentLocal.networkCondition !== newLocal.networkCondition ||
    currentRemote.networkCondition !== newRemote.networkCondition ||
    currentSource !== newSource
  );
};

const createNetworkConditionChecker = (deps = {}) => {
  const ee = new EventEmitter();
  const { session, subscriberId, stream } = deps;
  let lastNetworkConditionStats = null;
  let mantisSenderStats;
  let publisherSenderStats;
  let sourceStreamId;
  let congestionLevel = CongestionLevels.LOW;

  const getNetworkConditionStats = () => {
    const now = Date.now();
    if (mantisSenderStats && now - mantisSenderStats.clientTimeStamp >= SENDER_SIDE_STATS_TTL) {
      clearTimeout(mantisSenderStats.timeoutId);
      mantisSenderStats = null;
    }
    if (
      publisherSenderStats &&
      (
        publisherSenderStats.sourceStreamId !== sourceStreamId ||
        now - publisherSenderStats.clientTimeStamp >= SENDER_SIDE_STATS_TTL
      )
    ) {
      clearTimeout(publisherSenderStats.timeoutId);
      publisherSenderStats = null;
    }
    const networkConditionStats = {};
    const isP2P = sourceStreamId === 'P2P';
    const senderStats = isP2P ? publisherSenderStats : mantisSenderStats;
    if (senderStats) {
      const { maxBitrateBitsPerSecond, sentEstimatedBandwidthBitsPerSecond } = senderStats;
      networkConditionStats.senderStats = {
        connectionMaxAllocatedBitrate: maxBitrateBitsPerSecond,
        connectionEstimatedBandwidth: sentEstimatedBandwidthBitsPerSecond,
      };
    }
    if (isP2P) {
      const transport = {
        connectionEstimatedBandwidth:
          publisherSenderStats?.sentEstimatedBandwidthBitsPerSecond || -1,
        ...getNetworkCondition(publisherSenderStats, congestionLevel, stream, 'PUBLISHER', true, NETWORK_CONDITION_CODE.UNKNOWN),
      };
      networkConditionStats.transport = { ...transport };
      networkConditionStats.remotePublisherTransport = { ...transport };
      networkConditionStats.networkDegradationSource = getP2PNetworkDegradationSource(
        transport.networkCondition,
        publisherSenderStats,
        session,
        subscriberId
      );
    } else { // Routed
      const publisherNetworkCondition =
        publisherSenderStats?.networkCondition || NETWORK_CONDITION_CODE.UNKNOWN;
      networkConditionStats.transport = {
        connectionEstimatedBandwidth: mantisSenderStats?.sentEstimatedBandwidthBitsPerSecond || -1,
        ...getNetworkCondition(mantisSenderStats, congestionLevel, stream, 'MANTIS', false, publisherNetworkCondition),
      };
      networkConditionStats.remotePublisherTransport = {
        connectionEstimatedBandwidth:
          publisherSenderStats?.sentEstimatedBandwidthBitsPerSecond || -1,
        ...getNetworkCondition(publisherSenderStats, congestionLevel, stream, 'PUBLISHER', false, publisherNetworkCondition),
      };
      networkConditionStats.networkDegradationSource = getRoutedNetworkDegradationSource(
        networkConditionStats.transport.networkCondition,
        networkConditionStats.remotePublisherTransport.networkCondition,
        congestionLevel
      );
    }
    const networkConditionChanged =
      hasNetworkConditionChanged(lastNetworkConditionStats, networkConditionStats);
    lastNetworkConditionStats = networkConditionStats;
    return { networkConditionStats, networkConditionChanged };
  };

  const checkNetworkConditionChange = () => {
    ee.emit('networkConditionStatsChanged', getNetworkConditionStats());
  };

  const checkSenderStatsNetworkConditionChanged = (prevSenderStats, newSenderStats) => {
    if (prevSenderStats?.timeoutId) {
      clearTimeout(prevSenderStats.timeoutId);
    }
    // eslint-disable-next-line no-param-reassign
    newSenderStats.timeoutId = setTimeout(() => {
      checkNetworkConditionChange();
    }, SENDER_SIDE_STATS_TTL);
    if (
      !prevSenderStats ||
      prevSenderStats.networkCondition !== newSenderStats.networkCondition ||
      prevSenderStats.signalingNetworkScore !== newSenderStats.signalingNetworkScore ||
      prevSenderStats.maxBitrateBitsPerSecond !== newSenderStats.maxBitrateBitsPerSecond ||
      prevSenderStats.sentEstimatedBandwidthBitsPerSecond !==
        newSenderStats.sentEstimatedBandwidthBitsPerSecond ||
      prevSenderStats.sourceStreamId !== newSenderStats.sourceStreamId
    ) {
      checkNetworkConditionChange();
    }
  };

  const onSenderStatsReceived = (content) => {
    try {
      const parsedData = typeof content.data === 'string' ? JSON.parse(content.data) : content.data;
      if (sourceStreamId === 'P2P' && parsedData.sourceStreamId === 'P2P' && parsedData.subscriberId === subscriberId) {
        const prevStats = publisherSenderStats;
        publisherSenderStats = {
          ...parsedData,
          clientTimeStamp: Date.now(),
        };
        checkSenderStatsNetworkConditionChanged(prevStats, publisherSenderStats);
        return;
      }
      if (parsedData.sourceStreamId === 'MANTIS') {
        if (parsedData.subscriberId === subscriberId) {
          const prevStats = mantisSenderStats;
          mantisSenderStats = {
            ...parsedData,
            clientTimeStamp: Date.now(),
          };
          checkSenderStatsNetworkConditionChanged(prevStats, mantisSenderStats);
          return;
        }
        if (parsedData.subscriberId === 'MANTIS' && sourceStreamId === 'MANTIS') {
          const prevStats = publisherSenderStats;
          publisherSenderStats = {
            ...parsedData,
            clientTimeStamp: Date.now(),
          };
          checkSenderStatsNetworkConditionChanged(prevStats, publisherSenderStats);
          return;
        }
      }
    } catch (error) {
      logging.error(`Sender-Stats parsing failed: ${error.message}`);
    }
  };

  const setCongestionLevel = (level) => {
    if (level !== congestionLevel) {
      congestionLevel = level;
      if (mantisSenderStats) {
        delete mantisSenderStats.networkCondition;
        delete mantisSenderStats.networkConditionReason;
      }
      if (publisherSenderStats) {
        delete publisherSenderStats.networkCondition;
        delete publisherSenderStats.networkConditionReason;
      }
      checkNetworkConditionChange();
    }
  };

  const setSourceStreamId = (id) => {
    if (id !== sourceStreamId) {
      sourceStreamId = id;
      checkNetworkConditionChange();
    }
  };

  const destroy = () => {
    lastNetworkConditionStats = null;
    if (mantisSenderStats?.timeoutId) {
      clearTimeout(mantisSenderStats.timeoutId);
    }
    mantisSenderStats = null;
    if (publisherSenderStats?.timeoutId) {
      clearTimeout(publisherSenderStats.timeoutId);
    }
    publisherSenderStats = null;
    ee.removeAllListeners();
  };

  return Object.assign(ee, {
    onSenderStatsReceived,
    setCongestionLevel,
    setSourceStreamId,
    checkNetworkConditionChange,
    destroy,
  });
};

export default createNetworkConditionChecker;
